import React, { useEffect, useState } from 'react'
import { map } from 'lodash'
import styled from 'styled-components'
import { PrismicRichText } from '@prismicio/react'

import CardContainer from '../../src/components/CardContainer/CardContainer'
import { media } from '../../src/utils/style-utils'
import { backgroundColors } from '../../src/styles/backgroundColors'

const isLastRowElemNotNull = (columnNumber: number, row: any) => {
	switch (columnNumber) {
		case 3:
			return row.col_3_value !== null
		case 4:
			return row.col_4_value !== null
		case 5:
			return row.col_5_value !== null
		case 6:
			return row.col_6_value !== null
		case 7:
			return row.col_7_value !== null
		default:
			return true
	}
}

const PriceListSlice = ({ slice }) => {
	const columnNumber = Number(slice.primary.column_number) || 3
	const [isLastColumnVoid, setIsLastColumnVoid] = useState(true)
	const [groupedTables, setgroupedTables] = useState([])

	const statefulTable = slice.variation === 'statefulTable'
	const [selectedTable, setSelectedTable] = useState('')

	useEffect(() => {
		let firstItem = true
		const grouped = slice.items.reduce((acc, item) => {
			if (!acc[item.group_name_id]) {
				acc[item.group_name_id] = []
			}
			acc[item.group_name_id].push(item)

			firstItem && setSelectedTable(item.group_name_id)
			firstItem = false

			isLastRowElemNotNull(columnNumber, item) && setIsLastColumnVoid(false)

			return acc
		}, {})

		setgroupedTables(grouped)
	}, [columnNumber, slice.items])
	return (
		<StyledCardContainer
			options={{
				backgroundColor: backgroundColors(slice.primary.color),
			}}
		>
			<HeadingWrapper columnNumber={columnNumber}>
				{slice.primary.title && <h1>{slice.primary.title}</h1>}
				{statefulTable && (
					<>
						<PrismicRichText field={slice.primary.title_content} />
						<ButtonSelection>
							<h4>Veldu upphæð</h4>
							{map(groupedTables, (group: any, group_name) => {
								{
									return (
										<StatefulButton
											onClick={() => setSelectedTable(group[0].group_name_id)}
											selected={group[0].group_name_id === selectedTable}
										>
											{group[0].group_name_id}
										</StatefulButton>
									)
								}
							})}
						</ButtonSelection>
					</>
				)}
			</HeadingWrapper>
			<TablesContainer
				columnNumber={columnNumber}
				isLastColumnVoid={isLastColumnVoid}
				isStatefulTable={statefulTable}
			>
				{map(groupedTables, (group: any, group_name) => {
					return (
						<Table display={statefulTable ? group[0].group_name_id === selectedTable : true}>
							<thead>
								{statefulTable ? (
									<HeaderTable color={slice.primary.color}>
										<th>{slice.primary.column_1}</th>
										<th>{slice.primary.column_2}</th>
										<th>{slice.primary.column_3}</th>
									</HeaderTable>
								) : (
									<HeaderTable color={slice.primary.color}>
										<th>{group_name}</th>
										<th>{slice.primary.column_2}</th>
										{slice.primary.column_3 && columnNumber >= 3 && <th>{slice.primary.column_3}</th>}
										{slice.primary.column_4 && columnNumber >= 4 && <th>{slice.primary.column_4}</th>}
										{slice.primary.column_5 && columnNumber >= 5 && <th>{slice.primary.column_5}</th>}
										{slice.primary.column_6 && columnNumber >= 6 && <th>{slice.primary.column_6}</th>}
										{slice.primary.column_7 && columnNumber >= 7 && <th>{slice.primary.column_7}</th>}
									</HeaderTable>
								)}
							</thead>
							<TableBody columnNumber={columnNumber}>
								{map(group, (item: any) => {
									return (
										<tr>
											<td>{item.col_1_value}</td>
											<td>{item.col_2_value}</td>
											{columnNumber >= 3 && <td>{item.col_3_value}</td>}
											{columnNumber >= 4 && <td>{item.col_4_value}</td>}
											{columnNumber >= 5 && <td>{item.col_5_value}</td>}
											{columnNumber >= 6 && <td>{item.col_6_value}</td>}
											{columnNumber >= 7 && <td>{item.col_7_value}</td>}
										</tr>
									)
								})}
							</TableBody>
						</Table>
					)
				})}
			</TablesContainer>
		</StyledCardContainer>
	)
}

export default PriceListSlice

const Table = styled.table<{ display: boolean }>`
	${(prop) => !prop.display && 'display: none'};
`

const ButtonSelection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;

	h4 {
		font-weight: 700;
		margin: 0;
	}

	${media.tabletMd`
		margin-bottom: 30px;
	`}
`

const StatefulButton = styled.button<{ selected: boolean }>`
	border-radius: 32px;
	background: transparent;
	padding: 8px 24px;
	width: fit-content;
	border: 2px solid var(--deep-blue);
	cursor: pointer;

	font-family: var(--font-atyp-display);
	font-size: 18px;
	font-weight: 500;
	line-height: 32px;

	${(prop) =>
		prop.selected &&
		`
		color: var(--neon);
		background: var(--deep-blue);
	`}

	&:hover, &:focus {
		color: var(--neon);
		background: var(--deep-blue);
	}
`

const TableBody = styled.tbody<{ columnNumber: number }>`
	td {
		white-space: nowrap;
	}
	td:first-of-type {
		white-space: unset;
	}
`

const TablesContainer = styled.div<{ columnNumber: number; isLastColumnVoid: boolean; isStatefulTable: boolean }>`
	display: flex;
	flex-direction: column;
	grid-column: ${(props) => (props.columnNumber > 3 ? '5 / -2' : '6 / -2')};
	text-align: right;
	gap: 50px;
	font-weight: 400;
	* {
		font-family: var(--font-akzidenz);
	}

	table {
		border-collapse: collapse;
	}

	tr {
		height: 50px;
	}

	th,
	td {
		width: ${(prop) => (prop.isStatefulTable ? '30%' : `${100 / prop.columnNumber}%`)};
		min-width: ${(props) => (props.columnNumber > 3 ? 100 % (props.columnNumber + 1) : 30)}%;

		overflow: hidden;
		text-overflow: ellipsis;
		white-space: unset;
	}
	th:first-of-type,
	td:first-of-type {
		text-align: left;
		width: ${(prop) => prop.isStatefulTable && 'min-width'};
		${(prop) => prop.isStatefulTable && 'white-space: nowrap'};
	}

	${media.tablet`
		margin: 0 30px;
		grid-column: 2 / -2;
	`}

	${media.tabletMd`
		margin: 0;
	`}


	${media.tabletSm`
		grid-column: 2 / -2;

		th:first-of-type{
			font-size: 20px;
			line-height: 24px;
		}

		th{
			font-size: 14px;
			line-height: 16px;
		}

		td{
			font-size: 16px;
			line-height: 24px;
		}
	`}

	${media.phone`
		grid-column: 1 / -1;
		margin: 0 10px;

		tr{
			th:last-of-type, td:last-of-type{
				 ${(props) => props.isLastColumnVoid && 'display: none'};
			}
		}
	`}
`

const HeadingWrapper = styled.div<{ columnNumber: number }>`
	grid-column: ${(props) => (props.columnNumber > 3 ? '2 / 5' : '2 / 5')};
	height: fit-content;
	margin-bottom: ${(props) => props.columnNumber > 3 && '40px'};

	h1 {
		margin: 0;
		font-size: 3.5rem;
	}

	${media.tablet`
		margin: 0 0 40px 30px;
		grid-column: 2 / -2;

	`}

	${media.tabletMd`
		margin: 0;
	`}

	${media.tabletSm`

		h1{
			font-size: 32px;
			line-height: 40px
		}
	`}

	${media.phone`
		grid-column: 1 / -1;
		margin: 0 10px;
	`}
`

const StyledCardContainer = styled(CardContainer)`
	color: ${(props) => props.options.backgroundColor == backgroundColors('Deep Blue') && 'var(--off-white)'};
	padding-block: 9%;
`

const HeaderTable = styled.tr<{ color: string }>`
	border-bottom: 1px solid ${(props) => (props.color == 'Deep Blue' ? 'var(--off-white)' : 'var(--deep-blue)')};
	text-align: right;
	height: 30px;
`
